import { FC, useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Upload as UploadIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useUploadLogoHook } from "../../../hooks/useBrandingHook";
import { useInstituteContext } from "../../../context/institute.context";
import { logger } from "../../../utils/common";

interface LogoUploadDialogProps {
  open: boolean;
  onClose: () => void;
  existingLogoUrl?: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const SUPPORTED_FORMATS = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/svg+xml",
];
const RECOMMENDED_DIMENSIONS = { width: 200, height: 200 };

const LogoUploadDialog: FC<LogoUploadDialogProps> = ({
  open,
  onClose,
  existingLogoUrl,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const uploadLogoMutation = useUploadLogoHook();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    if (!open) {
      // Reset state when dialog closes
      setSelectedFile(null);
      setPreviewUrl(null);
      setValidationError(null);
      setUploadProgress(0);
      setImageDimensions(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!SUPPORTED_FORMATS.includes(file.type)) {
      return "Unsupported file format. Please upload PNG, JPG, or SVG files only.";
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB.`;
    }

    return null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      setSelectedFile(null);
      setPreviewUrl(null);
      setImageDimensions(null);
      return;
    }

    setValidationError(null);
    setSelectedFile(file);

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    // Get image dimensions for non-SVG files
    if (file.type !== "image/svg+xml") {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
      };
      img.src = url;
    } else {
      setImageDimensions(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedInstitute) {
      return;
    }

    try {
      await uploadLogoMutation.mutateAsync({
        request: {
          instituteId: selectedInstitute.id,
          file: selectedFile,
        },
        onProgress: (progress) => {
          setUploadProgress(progress);
        },
      });

      logger.show_success("Logo uploaded successfully!");
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to upload logo. Please try again.",
      );
    }
  };

  const handleCancel = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    onClose();
  };

  const isUploading = uploadLogoMutation.isPending;
  const hasFile = !!selectedFile;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      data-testid="logo-upload-dialog"
    >
      <DialogTitle>
        {existingLogoUrl ? "Replace Logo" : "Upload Logo"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {validationError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              data-testid="logo-validation-error"
            >
              {validationError}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Supported formats: PNG, JPG, SVG
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Maximum file size: {MAX_FILE_SIZE / (1024 * 1024)}MB
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Recommended dimensions: {RECOMMENDED_DIMENSIONS.width} x{" "}
              {RECOMMENDED_DIMENSIONS.height} pixels
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              data-testid="logo-file-input"
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              fullWidth
              disabled={isUploading}
              data-testid="select-logo-button"
            >
              {hasFile ? "Select Different File" : "Select Logo File"}
            </Button>
          </Box>

          {hasFile && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected File: {selectedFile?.name}
              </Typography>
              {imageDimensions && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Dimensions: {imageDimensions.width} x {imageDimensions.height}{" "}
                  pixels
                </Typography>
              )}
            </Box>
          )}

          {previewUrl && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview
              </Typography>
              <Grid container spacing={2}>
                {/* Full Logo Preview */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Full Logo
                      </Typography>
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          minHeight: 150,
                          bgcolor: "grey.100",
                          borderRadius: 1,
                          p: 2,
                        }}
                      >
                        <img
                          src={previewUrl}
                          alt="Logo preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: 150,
                            objectFit: "contain",
                          }}
                          data-testid="logo-preview-full"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Compact Logo Preview (as it might appear in header) */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Compact View (Header)
                      </Typography>
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          minHeight: 150,
                          bgcolor: "grey.100",
                          borderRadius: 1,
                          p: 2,
                        }}
                      >
                        <img
                          src={previewUrl}
                          alt="Logo preview compact"
                          style={{
                            maxWidth: "60%",
                            maxHeight: 60,
                            objectFit: "contain",
                          }}
                          data-testid="logo-preview-compact"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {isUploading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Uploading... {uploadProgress}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                data-testid="logo-upload-progress"
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          disabled={isUploading}
          startIcon={<CancelIcon />}
          data-testid="logo-upload-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!hasFile || isUploading || !!validationError}
          startIcon={<CheckCircleIcon />}
          data-testid="logo-upload-confirm-button"
        >
          {existingLogoUrl ? "Replace" : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogoUploadDialog;
